import React, { useContext, useEffect, useRef, useState } from 'react'
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep } from 'lodash';
import { genKey, getSelectedNodeWhileCreation, getUniqueWorkflowItemIdFromArray } from '../../../utils/utilFunctions';
import { treeActions } from '../../../redux/slices/treeSlice';
import { getRecordInsertingPosition } from './CopyAndPaste';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

function NewErrorHandler({ openModal, toggle }) {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx)
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const { leftTree } = useSelector(state => state.treeData[parentScreen]);
  const { account } = useSelector(state => state.queryParams);
  const { templateHeader } = useSelector(state => state.treeData.workflow.rightTree.tree)
  const currentTab = rightTree.currentTab;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false)
  const contentRef = useRef();
  const content = t('BOX_LIKE_TO_PLACE_ELEM_INSIDE_CURR_ELEM_OR_DIRECTLY_AFTER').split(`\\n`).join("<br />")

  const handleErrorHandler = (isInside) => {
    const exactNode = getSelectedNodeWhileCreation(selectedNode, rightTree?.tree?.[currentTab]?.translatedData || [], isInside);
    let nestedLevels = exactNode.key.split("-");
    nestedLevels = nestedLevels.splice(1);
    const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);
    let initialId = translatedData?.[0]?.ID;
    const newId = getUniqueWorkflowItemIdFromArray(translatedData);

    const getNewTranslateData = (data, key, isInside) => {
      const title = getErrorHandlerName(rightTree.tree);
      if (!data.length) {
        return { key: `${key}-0`, title, placingPosition: 1 };
      }

      let placingPosition;
      let newTranslateDataKey;

      const details = getRecordInsertingPosition(
        data,
        exactNode,
        isInside,
        "workflow"
      );
      placingPosition = details.index;
      newTranslateDataKey = details.key;
      return { key: newTranslateDataKey, title, placingPosition: placingPosition };
    }

    const insertNodeInNestedTree = (translatedData, nestedLevels, isInside) => {
      const level = isInside ? 0 : 1;
      if (nestedLevels.length === level) {
        const { children, key } = translatedData;
        const newErrorHandlerDetails = children ? getNewTranslateData(children, key, isInside) : getNewTranslateData(translatedData, 'wf', isInside);
        
        const newNode = {
          key: newErrorHandlerDetails.key,
          title: newErrorHandlerDetails.title,
          nodeId: genKey(newErrorHandlerDetails.title),
          alternateNodeId: genKey(newErrorHandlerDetails.title),
          CONCURRENT: 'FALSE',
          ENABLED: 'TRUE',
          ICON_DISPLAY: 'TRANSLATE',
          ID: `${newId}`,
          ACTION:"ERROR_HANDLER" ,
          ICON_DISPLAY:"ERROR_HANDLER" ,
          ARCHIVE_TRANSACTION_FILES:"TRUE" ,
          CONCURRENT:"FALSE" ,
          DISABLE_ERROR_HANDLER:"FALSE" ,
          NAME: newErrorHandlerDetails.title,
          OVERRIDE_WORKFLOW_STATUS:"FALSE" ,
          RETAIN_ALL_ERRORS:"FALSE" ,
          RETAIN_UNREPORTED_ERRORS:"FALSE" ,
          STOP_WORKFLOW:"FALSE",
          LOG_WFITEM_DETAILS: "TRUE"
        }
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: currentTab,
            data: {},
          })
        );
        const translatedDataArray = children ? translatedData.children : translatedData;
        translatedDataArray.splice(newErrorHandlerDetails.placingPosition, 0, newNode);
        setTimeout(() => {
          dispatch(treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: currentTab,
            data: { ...newNode }
          }));
        })
        return;
      }
      const isArray = Array.isArray(translatedData);

      if (isArray) {
        const nestedData = translatedData[[nestedLevels[0]]];
        const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
      } else {
        insertNodeInNestedTree(translatedData?.children, nestedLevels, isInside);
      }
    }

    insertNodeInNestedTree(translatedData, nestedLevels, isInside);
    const updatedData = getUpdatedData(translatedData, initialId);
    dispatch(treeActions.setNewRightTreeTranslatedData({
      screen: parentScreen,
      type: currentTab,
      data: updatedData
    }));
    toggle(prev => null);
    setShowModal(false);
  }

  const newTranslateDataHandler = () => {
    if (['LOOP_ITEM', 'CONDITION_IF', 'CONDITION_ELSE'].includes(selectedNode.ICON_DISPLAY) && openModal) {
      setShowModal(true);
    } else if (selectedNode.key === 'wf-0' && selectedNode.ICON_DISPLAY === 'OBJECTS') {
      handleErrorHandler(true);
      return;
    } else {
      handleErrorHandler(false);
      return;
    }
  }
  setTimeout(() => {
    if (contentRef.current?.innerHTML === "") contentRef.current.innerHTML = content
  }, 0)

  useEffect(() => {
    if (openModal) newTranslateDataHandler();
  }, [openModal]);

  return (
    <>
      <Dialog open={showModal} maxWidth="xs" fullWidth>
        <DialogTitle className='dialog-header'>
          <div className='title'>{t('BOX_CONFIRM')}</div>
        </DialogTitle>
        <DialogContent className='dialog-content' dividers>
          <div ref={contentRef}></div>
        </DialogContent>
        <DialogActions>
          <Button size='small' variant='outlined' color="primary" onClick={() => handleErrorHandler(true)}>
            {t('BTN_YES')}
          </Button>
          <Button size='small' variant='outlined' color="primary" onClick={() => handleErrorHandler(false)}>
            {t('BTN_NO')}
          </Button>
          <Button size='small' variant='outlined' color="primary" onClick={() => {
            toggle(null);
            setShowModal(false)
          }}>
            {t('BTN_CANCEL')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default NewErrorHandler


export const getNewName = (tree) => {
  const data = cloneDeep(tree);
  const body = data.body.translatedData;
  let titleNumber = 0;
  let hasPlainTranslateData = false; // Track if "Translate Data" exists without parentheses

  const getTitleName = (data) => {
    data.forEach((ele) => {
      if (ele?.title?.startsWith("Translate Data")) {
        const title = ele.title;
        const openParenIndex = title.indexOf("(");
        const closeParenIndex = title.indexOf(")");

        if (openParenIndex > -1 && closeParenIndex > openParenIndex) {
          // Extract the number between parentheses
          const numberPart = title.slice(openParenIndex + 1, closeParenIndex).trim();
          const parsedNumber = parseInt(numberPart, 10);

          if (!isNaN(parsedNumber)) {
            titleNumber = Math.max(titleNumber, parsedNumber);
          }
        } else {
          // If "Translate Data" exists without parentheses
          hasPlainTranslateData = true;
        }
      }

      if (ele?.children?.length > 0) {
        getTitleName(ele.children);
      }
    });
  };

  getTitleName(body);

  // If "Translate Data" exists without parentheses
  if (hasPlainTranslateData && titleNumber === 0) {
    return "Translate Data (1)";
  }

  return titleNumber === 0 ? "Translate Data" : `Translate Data (${titleNumber + 1})`;
};


const getErrorHandlerName = (tree) => {
  const data = cloneDeep(tree);
  const body = data.body.translatedData;
  let titleNumber = 0;
  let hasPlainErrorHandler = false; // Track if "Error Handler" exists without parentheses

  const getTitleName = (data) => {
    data.forEach((ele) => {
      if (ele?.title?.startsWith("Error Handler")) {
        const title = ele.title;
        const openParenIndex = title.indexOf("(");
        const closeParenIndex = title.indexOf(")");

        if (openParenIndex > -1 && closeParenIndex > openParenIndex) {
          // Extract the number between parentheses
          const numberPart = title.slice(openParenIndex + 1, closeParenIndex).trim();
          const parsedNumber = parseInt(numberPart, 10);

          if (!isNaN(parsedNumber)) {
            titleNumber = Math.max(titleNumber, parsedNumber);
          }
        } else {
          // If "Error Handler" exists without parentheses
          hasPlainErrorHandler = true;
        }
      }

      if (ele?.children?.length > 0) {
        getTitleName(ele.children);
      }
    });
  };

  getTitleName(body);

  // If "Error Handler" exists without parentheses
  if (hasPlainErrorHandler && titleNumber === 0) {
    return "Error Handler (1)";
  }

  return titleNumber === 0 ? "Error Handler" : `Error Handler (${titleNumber + 1})`;
};

export const getUpdatedData = (translatedData, initialId) => {
  const data = cloneDeep(translatedData);
  const key = 'wf'
  // let currentId = initialId;
  const getNewKeys = (data, key) => {
    data.forEach((ele, idx) => {
      const newKey = `${key}-${idx}`

      // if (ele?.ICON_DISPLAY === "GET_DATA") {
      //   ele.ID = currentId++; 
      // }
      if (ele.children?.length > 0) {
        if (data[idx]?.isLeaf !== undefined) {
          delete data[idx]?.isLeaf
        }
        getNewKeys(ele.children, newKey);
      }
    })
  }

  getNewKeys(data, key);
  return data;

}


